import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, onAuthStateChanged } from '../services/firebaseService';
import { useNavigate } from 'react-router-dom';
import { 
  signInWithGoogle, 
  logInWithEmailAndPassword, 
  registerWithEmailAndPassword, 
  sendPasswordReset, 
  logout,
  authenticateWithBackend,
  fetchUserProfile,
  logoutFromBackend,
  getAuthToken
} from "../firebase/firebase";
import { autoSubscribeToNewsletter } from '../services/userService';

// Create the Authentication Context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Authentication Provider Component
export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [token, setToken] = useState(null);

  // Set up auth state listener when the component mounts
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setLoading(true);
      if (currentUser) {
        // User is signed in with Firebase
        console.log('Firebase user authenticated:', currentUser);
        console.log('User photoURL:', currentUser.photoURL);
        
        // Ensure we have the latest user data with photoURL
        if (currentUser.reload) {
          await currentUser.reload();
          console.log('User data reloaded, photoURL:', currentUser.photoURL);
        }
        
        setUser(currentUser);
        
        // Authenticate with our backend
        console.log('Authenticating with backend...');
        const backendAuth = await authenticateWithBackend(currentUser);
        console.log('Backend auth result:', backendAuth);
        
        // Get the auth token
        const authToken = getAuthToken();
        setToken(authToken);
        console.log('Auth token retrieved:', authToken ? 'YES' : 'NO');
        
        // Fetch user profile from backend
        const profile = await fetchUserProfile();
        console.log('Backend profile:', profile);
        setUserProfile(profile);
        
        // Check if this is a new user registration
        // We can detect this by checking if currentUser.metadata.creationTime is close to currentUser.metadata.lastSignInTime
        const creationTime = new Date(currentUser.metadata.creationTime).getTime();
        const lastSignInTime = new Date(currentUser.metadata.lastSignInTime).getTime();
        const isNewUser = Math.abs(lastSignInTime - creationTime) < 60000; // Within 1 minute
        
        if (isNewUser) {
          // Auto-subscribe new users to the newsletter
          try {
            await autoSubscribeToNewsletter(currentUser);
            console.log('New user auto-subscribed to newsletter');
          } catch (error) {
            console.error('Failed to auto-subscribe user to newsletter:', error);
          }
        }
      } else {
        setUser(null);
        setUserProfile(null);
        setToken(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Enhanced register function that also handles newsletter subscription
  const handleRegister = async (email, password, displayName) => {
    const newUser = await registerWithEmailAndPassword(email, password, displayName);
    
    // Auto-subscribe the new user to the newsletter
    try {
      await autoSubscribeToNewsletter(newUser);
      console.log('New user auto-subscribed to newsletter');
    } catch (error) {
      console.error('Failed to auto-subscribe user to newsletter:', error);
    }
    
    return newUser;
  };

  const handleLogout = async () => {
    try {
      // Logout from backend first
      await logoutFromBackend();
      
      // Then logout from Firebase
      await logout();
      
      // Clear local state
      setUser(null);
      setUserProfile(null);
      setToken(null);

      // Navigate to homepage
      navigate('/');
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    isAuthenticated: !!user,
    isAdmin: userProfile?.role === 'admin',
    token,
    signInWithGoogle,
    logInWithEmailAndPassword,
    registerWithEmailAndPassword: handleRegister,
    sendPasswordReset,
    logout: handleLogout
  };

  // For debugging
  useEffect(() => {
    console.log('AuthContext state updated:', { 
      isAuthenticated: !!user,
      isAdmin: userProfile?.role === 'admin',
      userRole: userProfile?.role,
      hasToken: !!token,
      photoURL: user?.photoURL
    });
  }, [user, userProfile, token]);

  return (
    <AuthContext.Provider value={value}>
      {!loading ? children : <div>Loading...</div>}
    </AuthContext.Provider>
  );
}; 